<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import ContactAPI from 'dashboard/api/contacts';
import MeetingsAPI from 'dashboard/api/meetings';
import KanbanAPI from 'dashboard/api/kanban';
import MeetingComments from './MeetingComments.vue';
import { toSpDatetimeLocal, fromSpDatetimeLocal } from '../agendaDateUtils';

const props = defineProps({
  meetingId: { type: Number, default: null },
  defaultSlot: { type: Object, default: null },
});

const emit = defineEmits([
  'close',
  'created',
  'updated',
  'deleted',
  'openCard',
]);

const { t } = useI18n();

const store = useStore();
const { accountId } = useAccount();
const agents = useMapGetter('agents/getAgents');

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const meeting = computed(() => store.getters['agenda/currentMeeting']);
const isLoading = ref(false);
const hasLoadedOnce = ref(false);

const titleValue = ref('');
const notesValue = ref('');
const locationValue = ref('');
const localStartAt = ref('');
const localEndAt = ref('');
const locationInputRef = ref(null);

const activities = ref([]);

async function loadActivities() {
  if (!props.meetingId) return;
  try {
    const { data } = await MeetingsAPI.listActivities(props.meetingId);
    activities.value = data.data ?? [];
  } catch {
    activities.value = [];
  }
}

let creatingPromise = null;

async function createMeetingWithField(field, value) {
  const startAt = localStartAt.value
    ? fromSpDatetimeLocal(localStartAt.value)
    : props.defaultSlot?.start_at;
  const endAt = localEndAt.value
    ? fromSpDatetimeLocal(localEndAt.value)
    : props.defaultSlot?.end_at;
  if (!startAt || !endAt) return null;
  let created;
  try {
    created = await store.dispatch('agenda/createMeeting', {
      title: titleValue.value,
      start_at: startAt,
      end_at: endAt,
      [field]: value,
    });
  } catch {
    // eslint-disable-next-line no-alert
    window.alert(
      'Não foi possível criar a reunião. Verifique os dados e tente novamente.'
    );
    return null;
  }
  if (created) {
    store.commit('agenda/SET_MEETING', created);
    emit('created');
  }
  return created;
}

async function saveFields(fields) {
  if (!meeting.value) {
    const [field, value] = Object.entries(fields)[0];
    if (!creatingPromise) {
      creatingPromise = createMeetingWithField(field, value).finally(() => {
        creatingPromise = null;
      });
      await creatingPromise;
      return;
    }
    await creatingPromise;
    if (!meeting.value) return;
  }
  const updated = await store.dispatch('agenda/updateMeetingField', {
    id: meeting.value.id,
    ...fields,
  });
  if (!updated) {
    if ('start_at' in fields)
      localStartAt.value = toSpDatetimeLocal(meeting.value.start_at);
    if ('end_at' in fields)
      localEndAt.value = toSpDatetimeLocal(meeting.value.end_at);
    // eslint-disable-next-line no-alert
    window.alert(
      'Não foi possível salvar a alteração. Verifique os dados e tente novamente.'
    );
    return;
  }
  loadActivities();
  emit('updated');
}

async function saveField(field, value) {
  return saveFields({ [field]: value });
}

function onTitleBlur() {
  if (titleValue.value !== meeting.value?.title) {
    saveField('title', titleValue.value);
  }
}

function onNotesBlur() {
  if (notesValue.value !== meeting.value?.description) {
    saveField('description', notesValue.value);
  }
}

function onLocationBlur() {
  if (locationValue.value !== meeting.value?.location) {
    saveField('location', locationValue.value);
  }
}

// só abre como link endereço http(s): texto livre ("Escritório") vira edição
// e esquemas como javascript: nunca são executados
const isLocationUrl = computed(() =>
  /^https?:\/\//i.test(locationValue.value.trim())
);

function onLocationMousedown(e) {
  if (e.button !== 0 || !isLocationUrl.value) return;
  e.preventDefault();
  window.open(locationValue.value, '_blank', 'noopener,noreferrer');
}

function editLocation() {
  locationInputRef.value?.focus();
}

async function deleteMeeting() {
  if (!props.meetingId) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm('Excluir esta reunião?')) return;
  await store.dispatch('agenda/deleteMeeting', props.meetingId);
  emit('deleted');
  emit('close');
}

function formatDate(ts) {
  if (!ts) return null;
  const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
  return (
    d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }) +
    ' - ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

function formatDateTime(val) {
  if (!val) return '—';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yr = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${mo}/${yr}, ${h}:${m}`;
}

// ── status ────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  {
    key: 'scheduled',
    label: 'Agendada',
    color: 'text-n-blue-11 bg-n-blue-3 border-n-blue-6 hover:bg-n-blue-4',
  },
  {
    key: 'done',
    label: 'Realizada',
    color: 'text-n-teal-11 bg-n-teal-3 border-n-teal-6 hover:bg-n-teal-4',
  },
  {
    key: 'no_show',
    label: 'No-show',
    color: 'text-n-ruby-11 bg-n-ruby-3 border-n-ruby-6 hover:bg-n-ruby-4',
  },
  {
    key: 'rescheduled',
    label: 'Reagendada',
    color: 'text-n-amber-11 bg-n-amber-3 border-n-amber-6 hover:bg-n-amber-4',
  },
  {
    key: 'cancelled',
    label: 'Cancelada',
    color: 'text-n-slate-10 border-n-weak hover:bg-n-alpha-1',
    colorSelected: 'bg-n-alpha-2',
  },
];

const showStatusDropdown = ref(false);
const selectedStatus = computed(() =>
  STATUS_OPTIONS.find(o => o.key === meeting.value?.status)
);

async function setStatus(key) {
  showStatusDropdown.value = false;
  await saveField('status', key);
}

// ── responsável ───────────────────────────────────────────────────
const showAssigneeMenu = ref(false);
const assigneeSearch = ref('');
const localWatcherIds = ref([]);

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? []).filter(a => a.name.toLowerCase().includes(q));
});

const selectedAgent = computed(() =>
  (agents.value ?? []).find(a => a.id === meeting.value?.user_id)
);

async function setAssignee(agent) {
  showAssigneeMenu.value = false;
  assigneeSearch.value = '';
  await saveField('user_id', agent?.id ?? null);
  if (agent && localWatcherIds.value.includes(agent.id)) {
    localWatcherIds.value = localWatcherIds.value.filter(w => w !== agent.id);
    await saveField('watcher_ids', localWatcherIds.value);
  }
}

// ── contato ───────────────────────────────────────────────────────
const editingContact = ref(false);
const contactSearch = ref('');
const contactResults = ref([]);
const showContactDropdown = ref(false);
const contactHighlightedIndex = ref(0);
const contactInputRef = ref(null);
const isSelectingContact = ref(false);

const contactDisplayName = computed(() => meeting.value?.contact?.name || '');

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
  } catch {
    contactResults.value = [];
  }
}

async function selectContact(c) {
  isSelectingContact.value = false;
  showContactDropdown.value = false;
  editingContact.value = false;
  await saveField('contact_id', c.id);
}

function cancelContactEdit() {
  if (isSelectingContact.value) return;
  editingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
}

function openContactEdit() {
  contactSearch.value = contactDisplayName.value;
  editingContact.value = true;
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

function onContactKeydown(e) {
  if (!showContactDropdown.value) return;
  if (e.key === 'Escape') {
    cancelContactEdit();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    if (item) selectContact(item);
  }
}

watch(showContactDropdown, val => {
  if (val) window.addEventListener('keydown', onContactKeydown);
  else window.removeEventListener('keydown', onContactKeydown);
});

function openContactPage() {
  const contactId = meeting.value?.contact?.id;
  if (!contactId) return;
  window.open(
    `/app/accounts/${accountId.value}/contacts/${contactId}`,
    '_blank'
  );
}

// ── conversa vinculada ───────────────────────────────────────────
const isLoadingConversation = ref(false);
const contactConversations = ref([]);

async function loadContactConversations(contactId) {
  if (!contactId) return;
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    contactConversations.value = data?.payload ?? [];
  } catch {
    contactConversations.value = [];
  }
}

const hasConversation = computed(
  () => !!(meeting.value?.conversation_id || contactConversations.value.length)
);

async function openLastConversation() {
  if (isLoadingConversation.value) return;

  if (meeting.value?.conversation_id) {
    window.open(
      `/app/accounts/${accountId.value}/conversations/${meeting.value.conversation_id}`,
      '_blank'
    );
    return;
  }

  if (contactConversations.value.length) {
    const last = contactConversations.value.reduce((a, b) =>
      a.id > b.id ? a : b
    );
    window.open(
      `/app/accounts/${accountId.value}/conversations/${last.id}`,
      '_blank'
    );
    return;
  }

  const contactId = meeting.value?.contact?.id;
  if (!contactId) return;
  isLoadingConversation.value = true;
  try {
    const { data } = await ContactAPI.getConversations(contactId);
    const conversations = data?.payload ?? [];
    contactConversations.value = conversations;
    if (!conversations.length) return;
    const last = conversations.reduce((a, b) => (a.id > b.id ? a : b));
    window.open(
      `/app/accounts/${accountId.value}/conversations/${last.id}`,
      '_blank'
    );
  } finally {
    isLoadingConversation.value = false;
  }
}

// ── oportunidade vinculada ────────────────────────────────────────
const isLoadingCard = ref(false);
const contactCards = ref([]);

async function loadContactCards(contactId) {
  if (!contactId) return;
  try {
    const { data } = await KanbanAPI.getCardsByContact(contactId);
    contactCards.value = data?.data ?? [];
  } catch {
    contactCards.value = [];
  }
}

const hasOpportunity = computed(() => !!contactCards.value.length);

async function openLastOpportunity() {
  if (isLoadingCard.value) return;

  if (contactCards.value.length) {
    const target = contactCards.value.reduce((a, b) => (a.id > b.id ? a : b));
    emit('openCard', target);
    return;
  }

  const contactId = meeting.value?.contact?.id;
  if (!contactId) return;

  isLoadingCard.value = true;
  try {
    const { data } = await KanbanAPI.getCardsByContact(contactId);
    const cards = data?.data ?? [];
    contactCards.value = cards;
    if (!cards.length) return;
    const target = cards.reduce((a, b) => (a.id > b.id ? a : b));
    emit('openCard', target);
  } finally {
    isLoadingCard.value = false;
  }
}

// ── data/duração ──────────────────────────────────────────────────
const DURATION_OPTIONS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '1h', minutes: 60 },
  { label: '1h30', minutes: 90 },
  { label: '2h', minutes: 120 },
];

const showDatePicker = ref(false);
const showDurationMenu = ref(false);
const durationAlign = ref('left');
const statusDropdownAlign = ref('left');

const DURATION_LABELS = {
  15: '15 min',
  30: '30 min',
  45: '45 min',
  60: '1h',
  90: '1h30',
  120: '2h',
};
const pickerCalYear = ref(new Date().getFullYear());
const pickerCalMonth = ref(new Date().getMonth());
const useDuration = ref(false);
const selectedDuration = ref(60);
const showCustomDuration = ref(false);
const customDurationValue = ref(null);
const customDurationUnit = ref('minutes'); // 'minutes' | 'hours'
const showCustomUnitDropdown = ref(false);

const DAY_ABBR = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function pickerCalDays() {
  const year = pickerCalYear.value;
  const month = pickerCalMonth.value;
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i -= 1)
    cells.push({
      day: daysInPrev - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      outside: true,
    });
  for (let d = 1; d <= daysInMonth; d += 1)
    cells.push({ day: d, month, year, outside: false });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d += 1)
    cells.push({
      day: d,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      outside: true,
    });
  return cells;
}

const calDays = computed(pickerCalDays);

function isTodaySP(cell) {
  const now = new Date();
  const today = new Date(
    now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })
  );
  return (
    cell.day === today.getDate() &&
    cell.month === today.getMonth() &&
    cell.year === today.getFullYear() &&
    !cell.outside
  );
}

function isSelectedSP(cell) {
  const val = localStartAt.value;
  if (!val) return false;
  const [datePart] = val.split('T');
  const [yr, mo, dy] = datePart.split('-').map(Number);
  return (
    cell.day === dy &&
    cell.month === mo - 1 &&
    cell.year === yr &&
    !cell.outside
  );
}

function pickerTimeStr() {
  return localStartAt.value ? localStartAt.value.slice(11, 16) : '';
}

function applyDuration() {
  if (!localStartAt.value) return;
  const start = new Date(localStartAt.value);
  const end = new Date(start.getTime() + selectedDuration.value * 60000);
  localEndAt.value = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}T${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
}

function pickerSetDay(cell) {
  const mo = String(cell.month + 1).padStart(2, '0');
  const dy = String(cell.day).padStart(2, '0');
  const time = localStartAt.value ? localStartAt.value.slice(11, 16) : '09:00';
  localStartAt.value = `${cell.year}-${mo}-${dy}T${time}`;
  if (useDuration.value) applyDuration();
}

function pickerSetTime(val) {
  const datePart = localStartAt.value
    ? localStartAt.value.slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  localStartAt.value = `${datePart}T${val}`;
  if (useDuration.value) applyDuration();
}

// ── horário (input mascarado, padrão Tarefas) ───────────────────────
const timeRaw = ref('');
const timeInputEl = ref(null);

function parseTimeRaw(raw) {
  if (!raw || raw.length === 0) return null;
  const digits = raw.padEnd(4, '0');
  const h = parseInt(digits.slice(0, 2), 10);
  const m = parseInt(digits.slice(2, 4), 10);
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(raw) {
  if (!raw) return '';
  if (raw.length <= 2) return raw;
  return `${raw.slice(0, 2)}:${raw.slice(2)}`;
}

function syncTimeInputDisplay() {
  if (!timeInputEl.value) return;
  timeInputEl.value.value = formatTimeDisplay(timeRaw.value);
  const len = timeInputEl.value.value.length;
  timeInputEl.value.setSelectionRange(len, len);
}

function initTimeRaw() {
  timeRaw.value = pickerTimeStr().replace(':', '');
}

watch(localStartAt, () => {
  if (document.activeElement !== timeInputEl.value) initTimeRaw();
});

function commitTimeRaw() {
  const parsed = parseTimeRaw(timeRaw.value);
  if (parsed) pickerSetTime(parsed);
}

function onTimeKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitTimeRaw();
    timeInputEl.value?.blur();
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    initTimeRaw();
    syncTimeInputDisplay();
    timeInputEl.value?.blur();
    return;
  }

  if (e.key === 'Backspace') {
    e.preventDefault();
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    const display = formatTimeDisplay(timeRaw.value);
    const selEnd = el.selectionEnd;
    if (pos !== selEnd) {
      const digits = timeRaw.value.split('');
      let newRaw = '';
      let di = 0;
      for (let i = 0; i < display.length; i += 1) {
        if (display[i] !== ':') {
          if (i < pos || i >= selEnd) newRaw += digits[di];
          di += 1;
        }
      }
      timeRaw.value = newRaw;
    } else if (pos > 0) {
      let rawIdx = 0;
      for (let i = 0; i < pos; i += 1) {
        if (display[i] !== ':') rawIdx += 1;
      }
      rawIdx -= 1;
      if (rawIdx >= 0) {
        timeRaw.value =
          timeRaw.value.slice(0, rawIdx) + timeRaw.value.slice(rawIdx + 1);
      }
    }
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    let newPos = pos > 0 ? pos - 1 : 0;
    if (newPos > 0 && newDisplay[newPos - 1] === ':') newPos -= 1;
    el.setSelectionRange(newPos, newPos);
    if (parseTimeRaw(timeRaw.value)) commitTimeRaw();
    return;
  }

  if (/^\d$/.test(e.key)) {
    e.preventDefault();
    if (timeRaw.value.length >= 4) return;
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    const display = formatTimeDisplay(timeRaw.value);
    let rawIdx = 0;
    for (let i = 0; i < pos; i += 1) {
      if (display[i] !== ':') rawIdx += 1;
    }
    timeRaw.value =
      timeRaw.value.slice(0, rawIdx) + e.key + timeRaw.value.slice(rawIdx);
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    let newPos = pos + 1;
    if (newDisplay[newPos] === ':') newPos += 1;
    el.setSelectionRange(newPos, newPos);
    if (parseTimeRaw(timeRaw.value)) commitTimeRaw();
  }
}

function pickerSetQuick(type) {
  const now = new Date();
  const spNow = new Date(
    now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })
  );
  let base;
  if (type === 'today') {
    base = spNow;
  } else {
    base = new Date(spNow);
    base.setDate(base.getDate() + 1);
  }
  const yr = base.getFullYear();
  const mo = String(base.getMonth() + 1).padStart(2, '0');
  const dy = String(base.getDate()).padStart(2, '0');
  const time = localStartAt.value ? localStartAt.value.slice(11, 16) : '09:00';
  localStartAt.value = `${yr}-${mo}-${dy}T${time}`;
  pickerCalYear.value = yr;
  pickerCalMonth.value = base.getMonth();
  if (useDuration.value) applyDuration();
}

async function saveDateFromPicker() {
  showDatePicker.value = false;
  if (!localStartAt.value) return;
  const fields = { start_at: fromSpDatetimeLocal(localStartAt.value) };
  if (localEndAt.value) {
    fields.end_at = fromSpDatetimeLocal(localEndAt.value);
  }
  await saveFields(fields);
}

function applyCustomDuration() {
  const raw = customDurationValue.value;
  if (!raw || raw <= 0) return;
  const minutes = customDurationUnit.value === 'hours' ? raw * 60 : raw;
  selectedDuration.value = minutes;
  useDuration.value = true;
  applyDuration();
  showDurationMenu.value = false;
  showCustomDuration.value = false;
  showCustomUnitDropdown.value = false;
  saveDateFromPicker();
}

watch(selectedDuration, () => {
  if (useDuration.value) applyDuration();
});
watch(useDuration, val => {
  if (val) applyDuration();
});

function pickerPrevMonth() {
  if (pickerCalMonth.value === 0) {
    pickerCalMonth.value = 11;
    pickerCalYear.value -= 1;
  } else pickerCalMonth.value -= 1;
}
function pickerNextMonth() {
  if (pickerCalMonth.value === 11) {
    pickerCalMonth.value = 0;
    pickerCalYear.value += 1;
  } else pickerCalMonth.value += 1;
}
function pickerPrevYear() {
  pickerCalYear.value -= 1;
}
function pickerNextYear() {
  pickerCalYear.value += 1;
}

const datePickerAlign = ref('left');

function calcFlip(triggerEl, dropdownWidthPx) {
  if (!triggerEl) return 'left';
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  return rect.left + dropdownWidthPx > window.innerWidth - margin
    ? 'right'
    : 'left';
}

function openDatePickerFrom(triggerEl) {
  const val = localStartAt.value;
  if (val) {
    const [datePart] = val.split('T');
    const [yr, mo] = datePart.split('-').map(Number);
    pickerCalYear.value = yr;
    pickerCalMonth.value = mo - 1;
  }
  datePickerAlign.value = calcFlip(triggerEl, 288); // w-72
  initTimeRaw();
  showDatePicker.value = true;
}

// ── seguidores ────────────────────────────────────────────────────
const watchersOpen = ref(false);
const watchersSearch = ref('');
const watchersHighlight = ref(0);
const watchersTriggerRef = ref(null);
const watchersDropStyle = ref({});

const { avatarColor, initials } = useAvatarColor();

const agentOptions = computed(() =>
  (agents.value ?? []).map(a => ({
    id: a.id,
    label: a.name,
    avatar: a.thumbnail ?? null,
    color: a.color,
  }))
);

const watcherAgents = computed(() =>
  agentOptions.value.filter(a => localWatcherIds.value.includes(a.id))
);

const filteredWatcherAgents = computed(() => {
  const q = watchersSearch.value.toLowerCase().trim();
  if (!q) return agentOptions.value;
  return agentOptions.value.filter(a => a.label.toLowerCase().includes(q));
});

const watchersNavigable = computed(() => [
  ...filteredWatcherAgents.value.map(a => ({ id: a.id })),
  { id: 'clear' },
]);

function openWatchers() {
  watchersSearch.value = '';
  watchersHighlight.value = 0;
  const el = watchersTriggerRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    const dropW = 208;
    const left = Math.min(rect.left, window.innerWidth - dropW - 8);
    watchersDropStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 6}px`,
      left: `${left}px`,
      width: `${dropW}px`,
      zIndex: 200,
    };
  }
  watchersOpen.value = true;
}

async function toggleWatcher(id) {
  if (id === meeting.value?.user_id) return;
  const current = [...localWatcherIds.value];
  localWatcherIds.value = current.includes(id)
    ? current.filter(w => w !== id)
    : [...current, id];
  await saveField('watcher_ids', localWatcherIds.value);
}

function onWatchersKeydown(e) {
  if (e.key === 'Escape') {
    watchersOpen.value = false;
    watchersSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    watchersHighlight.value = Math.min(
      watchersHighlight.value + 1,
      watchersNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    watchersHighlight.value = Math.max(watchersHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = watchersNavigable.value[watchersHighlight.value];
    if (item?.id === 'clear') {
      localWatcherIds.value = [];
      saveField('watcher_ids', []);
      watchersOpen.value = false;
    } else if (item) toggleWatcher(item.id);
  } else if (e.key === 'Backspace') {
    watchersSearch.value = watchersSearch.value.slice(0, -1);
    watchersHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    watchersSearch.value += e.key;
    watchersHighlight.value = 0;
  }
}

watch(watchersOpen, val => {
  if (val) window.addEventListener('keydown', onWatchersKeydown);
  else {
    window.removeEventListener('keydown', onWatchersKeydown);
    watchersSearch.value = '';
  }
});

// ── convidados ────────────────────────────────────────────────────
const attendeeInput = ref('');
const attendeeInputRef = ref(null);
const localAttendeeEmails = ref([]);
const attendeeEmailError = ref('');

const automaticAttendeeEmails = computed(() => {
  const emails = [];
  if (selectedAgent.value?.email) emails.push(selectedAgent.value.email);
  (agents.value ?? [])
    .filter(a => localWatcherIds.value.includes(a.id))
    .forEach(a => {
      if (a.email) emails.push(a.email);
    });
  if (meeting.value?.contact?.email) emails.push(meeting.value.contact.email);
  return [...new Set(emails)];
});

const manualAttendeeEmails = computed(() =>
  localAttendeeEmails.value.filter(
    email => !automaticAttendeeEmails.value.includes(email)
  )
);

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function addAttendeeFromInput() {
  const email = attendeeInput.value.trim();
  if (!email) return;
  if (!isValidEmail(email)) {
    attendeeEmailError.value = 'E-mail inválido';
    return;
  }
  attendeeEmailError.value = '';
  if (!localAttendeeEmails.value.includes(email)) {
    localAttendeeEmails.value = [...localAttendeeEmails.value, email];
    saveField('attendee_emails', localAttendeeEmails.value);
  }
  attendeeInput.value = '';
}

function removeAttendee(email) {
  localAttendeeEmails.value = localAttendeeEmails.value.filter(
    e => e !== email
  );
  saveField('attendee_emails', localAttendeeEmails.value);
}

function onAttendeeKeydown(e) {
  if (e.key === 'Enter' || e.key === ',') {
    e.preventDefault();
    addAttendeeFromInput();
  } else if (
    e.key === 'Backspace' &&
    !attendeeInput.value &&
    localAttendeeEmails.value.length
  ) {
    attendeeEmailError.value = '';
    localAttendeeEmails.value = localAttendeeEmails.value.slice(0, -1);
    saveField('attendee_emails', localAttendeeEmails.value);
  } else {
    attendeeEmailError.value = '';
  }
}

// ── google meet ───────────────────────────────────────────────────
const googleMeetUrl = computed(() => meeting.value?.google_meet_url ?? '');

function openMeet() {
  if (googleMeetUrl.value) window.open(googleMeetUrl.value, '_blank');
}

watch(googleMeetUrl, url => {
  if (url && hasLoadedOnce.value && !isLoading.value && !locationValue.value) {
    locationValue.value = url;
    saveField('location', url);
  }
});

// ── histórico ─────────────────────────────────────────────────────
const STATUS_LABELS = {
  scheduled: 'Agendada',
  done: 'Realizada',
  no_show: 'No-show',
  rescheduled: 'Reagendada',
  cancelled: 'Cancelada',
};

const ACTIVITY_CONFIG = {
  meeting_created: {
    icon: 'i-lucide-plus-circle',
    color: 'text-n-teal-11',
    text: () => 'Reunião criada',
  },
  status_changed: {
    icon: 'i-lucide-check-circle',
    color: 'text-n-blue-11',
    text: a =>
      `Status alterado de ${STATUS_LABELS[a.old_value] ?? a.old_value ?? '—'} para ${STATUS_LABELS[a.new_value] ?? a.new_value ?? '—'}`,
  },
  title_changed: {
    icon: 'i-lucide-pencil',
    color: 'text-n-slate-10',
    text: () => 'Título atualizado',
  },
  assignee_changed: {
    icon: 'i-lucide-user-check',
    color: 'text-n-slate-10',
    text: () => 'Responsável alterado',
  },
  date_changed: {
    icon: 'i-lucide-calendar',
    color: 'text-n-slate-10',
    text: () => 'Data alterada',
  },
  contact_changed: {
    icon: 'i-lucide-user',
    color: 'text-n-slate-10',
    text: () => 'Contato alterado',
  },
  notes_changed: {
    icon: 'i-lucide-file-text',
    color: 'text-n-slate-10',
    text: () => 'Notas atualizadas',
  },
};

function activityConfig(eventType) {
  return (
    ACTIVITY_CONFIG[eventType] ?? {
      icon: 'i-lucide-activity',
      color: 'text-n-slate-10',
      text: () => eventType,
    }
  );
}

// ── load ──────────────────────────────────────────────────────────
async function loadMeeting() {
  if (!props.meetingId) {
    store.commit('agenda/SET_MEETING', null);
    titleValue.value = '';
    notesValue.value = '';
    locationValue.value = '';
    return;
  }
  isLoading.value = true;
  contactConversations.value = [];
  contactCards.value = [];
  try {
    await Promise.all([
      store.dispatch('agenda/fetchMeeting', props.meetingId),
      loadActivities(),
    ]);
    const m = store.getters['agenda/currentMeeting'];
    if (m) {
      titleValue.value = m.title ?? '';
      notesValue.value = m.description ?? '';
      locationValue.value = m.location ?? '';
      localWatcherIds.value = m.watcher_ids ?? [];
      localAttendeeEmails.value = m.attendee_emails ?? [];
      localStartAt.value = toSpDatetimeLocal(m.start_at);
      localEndAt.value = toSpDatetimeLocal(m.end_at);
      const contactId = m.contact?.id;
      if (contactId) {
        if (!m.conversation_id) loadContactConversations(contactId);
        loadContactCards(contactId);
      }
    }
  } finally {
    isLoading.value = false;
    hasLoadedOnce.value = true;
  }
}

watch(
  () => props.defaultSlot,
  slot => {
    if (slot && !props.meetingId) {
      localStartAt.value = toSpDatetimeLocal(slot.start_at);
      localEndAt.value = toSpDatetimeLocal(slot.end_at);
    }
  },
  { immediate: true }
);

function closeAllDropdowns() {
  showStatusDropdown.value = false;
  showAssigneeMenu.value = false;
  showDatePicker.value = false;
  showDurationMenu.value = false;
  watchersOpen.value = false;
}

onMounted(loadMeeting);
onUnmounted(() => {
  window.removeEventListener('keydown', onContactKeydown);
  window.removeEventListener('keydown', onWatchersKeydown);
});
watch(() => props.meetingId, loadMeeting);
</script>

<template>
  <div class="fixed inset-0 z-50 flex justify-end" @click.self="emit('close')">
    <div
      class="w-full h-full md:w-[540px] bg-n-background shadow-xl flex flex-col md:border-l border-n-weak"
      @click="closeAllDropdowns"
    >
      <!-- ── Header ── -->
      <div
        class="flex items-center justify-between px-5 py-3 border-b border-n-weak flex-shrink-0"
      >
        <div class="flex items-center gap-2" @click.stop>
          <button
            class="size-4 min-w-4 p-0 rounded flex items-center justify-center transition-all"
            :class="
              googleMeetUrl
                ? 'text-n-teal-11 hover:text-n-teal-10'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              googleMeetUrl
                ? t('AGENDA.MODAL.OPEN_GOOGLE_MEET')
                : t('AGENDA.MODAL.NO_MEETING_LINK')
            "
            @click="openMeet"
          >
            <span class="i-lucide-arrow-up-right size-4" />
          </button>

          <span v-if="localStartAt" class="text-xs text-n-slate-9">
            {{
              (() => {
                const [yr, mo, dy] = localStartAt.slice(0, 10).split('-');
                const date = `${dy}/${mo}/${yr.slice(2)}`;
                const start = localStartAt.slice(11, 16);
                const end = localEndAt ? ` - ${localEndAt.slice(11, 16)}` : '';
                return `${date} · ${start}${end}`;
              })()
            }}
          </span>
        </div>

        <div class="flex items-center gap-1">
          <button
            class="p-1.5 transition-colors"
            :class="
              meeting?.contact?.id
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              meeting?.contact?.id
                ? t('AGENDA.MODAL.OPEN_CONTACT')
                : t('AGENDA.MODAL.NO_LINKED_CONTACT')
            "
            @click.stop="meeting?.contact?.id ? openContactPage() : undefined"
          >
            <span class="i-lucide-user size-4" />
          </button>

          <button
            class="p-1.5 transition-colors"
            :class="
              hasConversation
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              meeting?.conversation_id
                ? t('AGENDA.MODAL.OPEN_LINKED_CONVERSATION')
                : hasConversation
                  ? t('AGENDA.MODAL.OPEN_LAST_CONVERSATION')
                  : t('AGENDA.MODAL.NO_LINKED_CONVERSATION')
            "
            @click.stop="hasConversation ? openLastConversation() : undefined"
          >
            <span
              class="size-4"
              :class="
                isLoadingConversation
                  ? 'i-lucide-loader animate-spin'
                  : 'i-lucide-message-square'
              "
            />
          </button>

          <button
            class="p-1.5 transition-colors"
            :class="
              hasOpportunity
                ? 'text-n-slate-12 hover:text-n-brand'
                : 'text-n-slate-6 cursor-default'
            "
            :title="
              hasOpportunity
                ? t('AGENDA.MODAL.OPEN_LAST_OPPORTUNITY')
                : t('AGENDA.MODAL.NO_LINKED_OPPORTUNITY')
            "
            @click.stop="hasOpportunity ? openLastOpportunity() : undefined"
          >
            <span
              class="size-4"
              :class="
                isLoadingCard
                  ? 'i-lucide-loader animate-spin'
                  : 'i-lucide-filter'
              "
            />
          </button>

          <button
            class="p-1.5 text-n-slate-9 hover:text-n-ruby-9 transition-colors"
            :title="t('AGENDA.MODAL.DELETE')"
            @click.stop="deleteMeeting"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>

          <button
            class="p-1.5 text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="emit('close')"
          >
            <span
              :class="isMobile ? 'i-lucide-chevron-left' : 'i-lucide-x'"
              class="size-4"
            />
          </button>
        </div>
      </div>

      <!-- ── Loading ── -->
      <div v-if="isLoading" class="flex-1 flex items-center justify-center">
        <span class="i-lucide-loader size-6 text-n-slate-9 animate-spin" />
      </div>

      <!-- ── Body ── -->
      <div v-else class="flex-1 overflow-y-auto">
        <div class="px-5 py-3 flex flex-col gap-3">
          <!-- Título -->
          <input
            v-model="titleValue"
            type="text"
            autofocus
            :placeholder="t('AGENDA.MODAL.TITLE')"
            class="reset-base w-full text-lg font-semibold text-n-slate-12 bg-transparent focus:outline-none placeholder:text-n-slate-9 p-0"
            @blur="onTitleBlur"
          />

          <!-- Grid de metadados 3x2 -->
          <div class="border border-n-weak rounded-lg">
            <!-- Linha 1: Contato | Responsável -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak border-b border-n-weak"
            >
              <!-- Contato -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.CONTACT') }}
                </p>
                <div class="relative">
                  <div v-if="editingContact" class="flex items-center gap-2">
                    <span
                      class="i-lucide-search size-3.5 text-n-slate-9 flex-shrink-0"
                    />
                    <input
                      ref="contactInputRef"
                      v-model="contactSearch"
                      class="flex-1 text-sm text-n-slate-12 bg-transparent outline-none border-b border-n-weak min-w-0"
                      :placeholder="t('AGENDA.MODAL.CONTACT_PLACEHOLDER')"
                      @input="searchContacts($event.target.value)"
                      @blur="cancelContactEdit"
                    />
                  </div>
                  <button
                    v-else
                    class="flex items-center gap-2 w-full text-left group/contact"
                    @click="openContactEdit"
                  >
                    <div
                      class="size-6 min-w-6 rounded-full bg-n-surface-2 overflow-hidden flex items-center justify-center flex-shrink-0"
                    >
                      <img
                        v-if="meeting?.contact?.thumbnail"
                        :src="meeting.contact.thumbnail"
                        :alt="meeting.contact.name"
                        class="size-full object-cover"
                      />
                      <span
                        v-else
                        class="text-[10px] font-bold text-n-slate-11 flex items-center justify-center size-full"
                      >
                        {{ initials(meeting?.contact?.name ?? '') }}
                      </span>
                    </div>
                    <span
                      class="text-sm text-n-slate-12 truncate flex-1 leading-none"
                    >
                      {{ contactDisplayName || '—' }}
                    </span>
                    <span
                      class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/contact:opacity-60 transition-opacity flex-shrink-0"
                    />
                  </button>
                  <div
                    v-if="showContactDropdown && contactResults.length"
                    class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-44 overflow-y-auto w-full min-w-[200px]"
                    @mousedown="isSelectingContact = true"
                  >
                    <button
                      v-for="(c, idx) in contactResults"
                      :key="c.id"
                      type="button"
                      class="w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors"
                      :class="
                        idx === contactHighlightedIndex
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : 'text-n-slate-12 hover:bg-n-alpha-1'
                      "
                      @click="selectContact(c)"
                    >
                      <div
                        class="size-6 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white bg-n-slate-5"
                        :style="
                          c.thumbnail
                            ? {
                                backgroundImage: `url(${c.thumbnail})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }
                            : {}
                        "
                      >
                        <span v-if="!c.thumbnail">{{
                          initials(c.company_name || c.name)
                        }}</span>
                      </div>
                      <div class="min-w-0">
                        <p class="truncate">{{ c.name }}</p>
                        <p
                          v-if="c.company_name"
                          class="text-n-slate-9 text-xs truncate"
                        >
                          {{ c.company_name }}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Responsável -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.ASSIGNEE') }}
                </p>
                <button
                  class="flex items-center gap-2 w-full text-left outline-none focus:outline-none"
                  @click="
                    showAssigneeMenu
                      ? (showAssigneeMenu = false)
                      : (showAssigneeMenu = true)
                  "
                >
                  <div
                    class="size-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
                    :style="
                      selectedAgent
                        ? selectedAgent.thumbnail
                          ? {
                              backgroundImage: `url(${selectedAgent.thumbnail})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }
                          : { backgroundColor: avatarColor(selectedAgent) }
                        : {}
                    "
                    :class="
                      !selectedAgent
                        ? 'bg-n-surface-2 border border-n-weak'
                        : ''
                    "
                  >
                    <span v-if="selectedAgent && !selectedAgent.thumbnail">{{
                      initials(selectedAgent.name)
                    }}</span>
                    <span
                      v-else-if="!selectedAgent"
                      class="i-lucide-user-plus size-3 text-n-slate-9"
                    />
                  </div>
                  <span
                    class="text-sm text-n-slate-12 truncate flex-1 leading-none"
                  >
                    {{ selectedAgent?.name || '—' }}
                  </span>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="showAssigneeMenu"
                  class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[180px] py-1 overflow-hidden"
                  @click.stop
                >
                  <input
                    v-model="assigneeSearch"
                    type="text"
                    :placeholder="t('AGENDA.MODAL.ASSIGNEE_SEARCH_PLACEHOLDER')"
                    class="w-full px-3 py-1.5 text-sm border-b border-n-weak focus:outline-none bg-transparent text-n-slate-12 placeholder:text-n-slate-9"
                    @click.stop
                  />
                  <button
                    class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                    :class="
                      !meeting?.user_id
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-11'
                    "
                    @click="setAssignee(null)"
                  >
                    <span class="i-lucide-user-x size-3.5 mr-2 flex-shrink-0" />
                    {{ t('AGENDA.MODAL.ASSIGNEE_NONE') }}
                  </button>
                  <button
                    v-for="agent in filteredAgents"
                    :key="agent.id"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                    :class="
                      meeting?.user_id === agent.id
                        ? 'text-n-brand font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="setAssignee(agent)"
                  >
                    <div
                      class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
                      :style="
                        agent.thumbnail
                          ? {
                              backgroundImage: `url(${agent.thumbnail})`,
                              backgroundSize: 'cover',
                            }
                          : { backgroundColor: avatarColor(agent) }
                      "
                    >
                      <span v-if="!agent.thumbnail">{{
                        initials(agent.name)
                      }}</span>
                    </div>
                    <span class="truncate">{{ agent.name }}</span>
                    <span
                      v-if="meeting?.user_id === agent.id"
                      class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
                    />
                  </button>
                </div>
              </div>
            </div>

            <!-- Linha 2: Data | Seguidores -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak border-b border-n-weak"
            >
              <!-- Data/Hora início -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.DATE') }}
                </p>
                <div class="relative">
                  <button
                    class="flex items-center gap-1.5 text-sm transition-colors text-n-slate-12 hover:text-n-brand"
                    @click="
                      showDatePicker
                        ? (showDatePicker = false)
                        : openDatePickerFrom($event.currentTarget)
                    "
                  >
                    <span class="i-lucide-calendar size-3.5 flex-shrink-0" />
                    <span>{{
                      localStartAt
                        ? formatDateTime(new Date(localStartAt))
                        : t('AGENDA.MODAL.SELECT_DATE')
                    }}</span>
                  </button>

                  <!-- Date picker inline -->
                  <div
                    v-if="showDatePicker"
                    class="absolute top-8 z-50 bg-n-background border border-n-weak rounded-xl shadow-xl w-72"
                    :class="datePickerAlign === 'right' ? 'right-0' : 'left-0'"
                    @click.stop
                  >
                    <div class="flex border-b border-n-weak">
                      <button
                        class="flex-1 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-brand transition-colors"
                        @click="pickerSetQuick('today')"
                      >
                        {{ t('AGENDA.MODAL.TODAY') }}
                      </button>
                      <button
                        class="flex-1 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-brand transition-colors border-l border-n-weak"
                        @click="pickerSetQuick('tomorrow')"
                      >
                        {{ t('AGENDA.MODAL.TOMORROW') }}
                      </button>
                    </div>
                    <div class="flex items-center justify-between px-3 py-2">
                      <button
                        class="p-1 hover:bg-n-alpha-1 rounded"
                        @click="pickerPrevYear"
                      >
                        <span
                          class="i-lucide-chevrons-left size-3.5 text-n-slate-10"
                        />
                      </button>
                      <button
                        class="p-1 hover:bg-n-alpha-1 rounded"
                        @click="pickerPrevMonth"
                      >
                        <span
                          class="i-lucide-chevron-left size-3.5 text-n-slate-10"
                        />
                      </button>
                      <span class="text-sm font-semibold text-n-slate-12">
                        {{ MONTH_NAMES[pickerCalMonth] }}
                        {{ pickerCalYear }}
                      </span>
                      <button
                        class="p-1 hover:bg-n-alpha-1 rounded"
                        @click="pickerNextMonth"
                      >
                        <span
                          class="i-lucide-chevron-right size-3.5 text-n-slate-10"
                        />
                      </button>
                      <button
                        class="p-1 hover:bg-n-alpha-1 rounded"
                        @click="pickerNextYear"
                      >
                        <span
                          class="i-lucide-chevrons-right size-3.5 text-n-slate-10"
                        />
                      </button>
                    </div>
                    <div class="px-2 pb-1">
                      <div class="grid grid-cols-7 mb-1">
                        <span
                          v-for="d in DAY_ABBR"
                          :key="d"
                          class="text-center text-[10px] font-medium text-n-slate-9 py-1"
                        >
                          {{ d }}
                        </span>
                      </div>
                      <div class="grid grid-cols-7 gap-y-0.5">
                        <button
                          v-for="(cell, i) in calDays"
                          :key="i"
                          class="aspect-square flex items-center justify-center text-xs rounded-lg transition-colors"
                          :class="[
                            cell.outside ? 'text-n-slate-8' : 'text-n-slate-12',
                            isSelectedSP(cell)
                              ? 'bg-n-brand text-white font-semibold'
                              : isTodaySP(cell)
                                ? 'text-n-brand font-semibold hover:bg-n-alpha-1'
                                : 'hover:bg-n-alpha-1',
                          ]"
                          @click="pickerSetDay(cell)"
                        >
                          {{ cell.day }}
                        </button>
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-2 px-3 py-2 border-t border-n-weak"
                    >
                      <span
                        class="i-lucide-clock size-3.5 flex-shrink-0"
                        :class="
                          timeRaw.length >= 1
                            ? 'text-n-brand'
                            : 'text-n-slate-9'
                        "
                      />
                      <div
                        class="inline-flex items-center gap-2 px-3 py-1 cursor-text bg-n-surface-2 rounded-lg"
                        @click="timeInputEl && timeInputEl.focus()"
                      >
                        <input
                          ref="timeInputEl"
                          type="text"
                          inputmode="numeric"
                          maxlength="5"
                          size="5"
                          :value="formatTimeDisplay(timeRaw)"
                          class="reset-base bg-n-surface-2 text-sm font-medium text-n-slate-12 focus:outline-none bg-none border-0 p-0 m-0 shadow-none w-12 h-5 leading-5 block"
                          @keydown="onTimeKeydown"
                        />
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-1 px-3 py-2 border-t border-n-weak flex-wrap"
                    >
                      <button
                        v-for="(label, dur) in DURATION_LABELS"
                        :key="dur"
                        class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border transition-colors"
                        :class="
                          useDuration && selectedDuration === Number(dur)
                            ? 'border-n-brand text-n-brand bg-n-alpha-2'
                            : 'border-n-weak text-n-slate-11 hover:border-n-container'
                        "
                        @click="
                          selectedDuration = Number(dur);
                          useDuration = true;
                        "
                      >
                        <span class="i-lucide-timer size-3 flex-shrink-0" />{{
                          label
                        }}
                      </button>
                      <button
                        v-if="useDuration"
                        class="ml-auto text-n-slate-9 hover:text-n-slate-12"
                        @click="useDuration = false"
                      >
                        <span class="i-lucide-x size-3" />
                      </button>
                    </div>
                    <div
                      class="px-3 py-2 border-t border-n-weak flex justify-end"
                    >
                      <button
                        class="px-3 py-1.5 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 transition-all"
                        @click="saveDateFromPicker"
                      >
                        {{ t('AGENDA.MODAL.CONFIRM') }}
                      </button>
                    </div>
                  </div>
                  <div
                    v-if="showDatePicker"
                    class="fixed inset-0 z-40"
                    @click="showDatePicker = false"
                  />
                </div>
              </div>

              <!-- Seguidores -->
              <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.FOLLOWERS') }}
                </p>
                <div class="relative">
                  <button
                    ref="watchersTriggerRef"
                    class="flex items-center gap-1 w-full text-left outline-none focus:outline-none min-h-[24px]"
                    @click="
                      watchersOpen ? (watchersOpen = false) : openWatchers()
                    "
                  >
                    <div v-if="watcherAgents.length" class="flex items-center">
                      <div
                        v-for="(agent, idx) in watcherAgents"
                        :key="agent.id"
                        class="size-6 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white overflow-hidden border border-n-background"
                        :class="idx > 0 ? '-ml-1.5' : ''"
                        :style="
                          agent.avatar
                            ? {
                                backgroundImage: `url(${agent.avatar})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }
                            : { backgroundColor: avatarColor(agent) }
                        "
                      >
                        <span v-if="!agent.avatar">{{
                          initials(agent.label)
                        }}</span>
                      </div>
                      <span
                        class="i-lucide-user-plus size-3.5 text-n-slate-9 ml-1.5 flex-shrink-0"
                      />
                    </div>
                    <span
                      v-else
                      class="i-lucide-user-plus size-4 text-n-slate-9"
                    />
                  </button>

                  <div
                    v-if="watchersOpen"
                    class="z-[200] bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
                    :style="watchersDropStyle"
                    @click.stop
                  >
                    <button
                      v-for="agent in filteredWatcherAgents"
                      :key="agent.id"
                      class="w-full flex items-center justify-start gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-left"
                      :class="
                        watchersNavigable[watchersHighlight]?.id === agent.id
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : localWatcherIds.includes(agent.id)
                            ? 'text-n-brand font-medium'
                            : agent.id === meeting?.user_id
                              ? 'text-n-slate-10'
                              : 'text-n-slate-12'
                      "
                      @click="toggleWatcher(agent.id)"
                    >
                      <div
                        class="size-5 rounded-full flex-shrink-0 flex items-center justify-center text-[7.5px] font-bold text-white overflow-hidden"
                        :style="
                          agent.avatar
                            ? {
                                backgroundImage: `url(${agent.avatar})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }
                            : { backgroundColor: avatarColor(agent) }
                        "
                      >
                        <span v-if="!agent.avatar">{{
                          initials(agent.label)
                        }}</span>
                      </div>
                      <span class="truncate flex-1">{{ agent.label }}</span>
                      <span
                        v-if="agent.id === meeting?.user_id"
                        class="text-[10px] text-n-slate-9 ml-auto flex-shrink-0 italic"
                      >
                        {{ t('AGENDA.MODAL.ASSIGNEE_LABEL') }}
                      </span>
                      <span
                        v-else-if="localWatcherIds.includes(agent.id)"
                        class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
                      />
                    </button>
                    <p
                      v-if="watchersSearch && !filteredWatcherAgents.length"
                      class="px-3 py-2 text-sm text-n-slate-10 italic"
                    >
                      {{ t('AGENDA.MODAL.SEARCH_NOTHING_FOUND') }}
                    </p>
                    <div class="border-t border-n-weak my-1" />
                    <button
                      class="w-full flex items-center justify-start gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-left"
                      :class="
                        watchersNavigable[watchersHighlight]?.id === 'clear'
                          ? 'text-n-brand font-medium bg-n-alpha-1'
                          : !localWatcherIds.length
                            ? 'text-n-brand font-medium'
                            : 'text-n-slate-11'
                      "
                      @click="
                        localWatcherIds = [];
                        saveField('watcher_ids', []);
                        watchersOpen = false;
                      "
                    >
                      <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
                      {{ t('AGENDA.MODAL.NO_FOLLOWERS') }}
                    </button>
                  </div>

                  <div
                    v-if="watchersOpen"
                    class="fixed inset-0 z-20"
                    @click="watchersOpen = false"
                  />
                </div>
              </div>
            </div>

            <!-- Linha 3: Duração | Status -->
            <div
              class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-n-weak"
            >
              <!-- Duração -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.DURATION') }}
                </p>
                <button
                  class="flex items-center gap-1.5 text-sm text-n-slate-12 hover:text-n-brand transition-colors"
                  @click="
                    showDurationMenu
                      ? (showDurationMenu = false)
                      : ((durationAlign = calcFlip($event.currentTarget, 144)),
                        (showDurationMenu = true),
                        (showCustomDuration = false),
                        (customDurationValue = null),
                        (customDurationUnit = 'minutes'))
                  "
                >
                  <span class="i-lucide-timer size-3.5 flex-shrink-0" />
                  <span>
                    {{
                      localStartAt && localEndAt
                        ? (() => {
                            const diff = Math.round(
                              (new Date(localEndAt) - new Date(localStartAt)) /
                                60000
                            );
                            if (diff <= 0) return '—';
                            if (diff < 60) return `${diff} min`;
                            const h = Math.floor(diff / 60);
                            const m = diff % 60;
                            return m
                              ? `${h}h${String(m).padStart(2, '0')}`
                              : `${h}h`;
                          })()
                        : '—'
                    }}
                  </span>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                  />
                </button>
                <div
                  v-if="showDurationMenu"
                  class="absolute top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-48"
                  :class="durationAlign === 'right' ? 'right-0' : 'left-0'"
                  @click.stop
                >
                  <template v-if="!showCustomDuration">
                    <button
                      v-for="opt in DURATION_OPTIONS"
                      :key="opt.minutes"
                      class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                      @click="
                        selectedDuration = opt.minutes;
                        useDuration = true;
                        applyDuration();
                        showDurationMenu = false;
                        saveDateFromPicker();
                      "
                    >
                      {{ opt.label }}
                    </button>
                    <button
                      class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-n-slate-12 border-t border-n-weak"
                      @click="showCustomDuration = true"
                    >
                      {{ t('AGENDA.MODAL.CUSTOM_DURATION') }}
                    </button>
                  </template>
                  <div v-else class="px-2 py-2">
                    <button
                      class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors mb-2"
                      @click="showCustomDuration = false"
                    >
                      <span class="i-lucide-arrow-left size-3" />
                      {{ t('AGENDA.MODAL.CUSTOM_DURATION_BACK') }}
                    </button>
                    <div class="flex items-center gap-2">
                      <input
                        v-model.number="customDurationValue"
                        type="number"
                        min="1"
                        class="reset-base w-16 px-2 py-1.5 text-sm bg-n-surface-2 border border-n-weak rounded-lg text-n-slate-12 focus:outline-none focus:border-n-brand"
                        @keydown.enter="applyCustomDuration"
                        @blur="applyCustomDuration"
                      />
                      <div class="relative flex-1">
                        <button
                          class="w-full flex items-center justify-between gap-1 px-2.5 py-1.5 text-sm border border-n-weak rounded-lg text-n-slate-12 bg-n-background hover:bg-n-alpha-1 transition-colors"
                          @click="
                            showCustomUnitDropdown = !showCustomUnitDropdown
                          "
                        >
                          {{
                            customDurationUnit === 'hours'
                              ? t('AGENDA.MODAL.CUSTOM_DURATION_HOURS')
                              : t('AGENDA.MODAL.CUSTOM_DURATION_MINUTES')
                          }}
                          <span
                            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
                          />
                        </button>
                        <div
                          v-if="showCustomUnitDropdown"
                          class="absolute top-full mt-1 right-0 z-40 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-full min-w-[100px]"
                          @click.stop
                        >
                          <button
                            class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                            :class="
                              customDurationUnit === 'minutes'
                                ? 'text-n-brand font-medium'
                                : 'text-n-slate-12'
                            "
                            @mousedown.prevent="
                              customDurationUnit = 'minutes';
                              showCustomUnitDropdown = false;
                            "
                          >
                            {{ t('AGENDA.MODAL.CUSTOM_DURATION_MINUTES') }}
                          </button>
                          <button
                            class="w-full flex items-center px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
                            :class="
                              customDurationUnit === 'hours'
                                ? 'text-n-brand font-medium'
                                : 'text-n-slate-12'
                            "
                            @mousedown.prevent="
                              customDurationUnit = 'hours';
                              showCustomUnitDropdown = false;
                            "
                          >
                            {{ t('AGENDA.MODAL.CUSTOM_DURATION_HOURS') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
                <p
                  class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('AGENDA.MODAL.STATUS') }}
                </p>
                <button
                  class="flex items-center gap-1 text-left outline-none focus:outline-none"
                  @click="
                    showStatusDropdown
                      ? (showStatusDropdown = false)
                      : ((statusDropdownAlign = calcFlip(
                          $event.currentTarget,
                          160
                        )),
                        (showStatusDropdown = true))
                  "
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium transition-colors"
                    :class="
                      selectedStatus?.color ??
                      'text-n-slate-10 bg-n-surface-2 border-n-weak'
                    "
                  >
                    {{ selectedStatus?.label ?? 'Status' }}
                    <span class="i-lucide-chevron-down size-3 opacity-70" />
                  </span>
                </button>
                <div
                  v-if="showStatusDropdown"
                  class="absolute top-full mt-1 z-40 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 min-w-[160px]"
                  :class="
                    statusDropdownAlign === 'right' ? 'right-0' : 'left-0'
                  "
                  @click.stop
                >
                  <button
                    v-for="opt in STATUS_OPTIONS"
                    :key="opt.key"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm transition-colors"
                    :class="[
                      opt.color,
                      meeting?.status === opt.key && opt.colorSelected
                        ? opt.colorSelected
                        : '',
                      meeting?.status === opt.key
                        ? 'font-semibold'
                        : 'font-normal',
                    ]"
                    @click="setStatus(opt.key)"
                  >
                    {{ opt.label }}
                    <span
                      v-if="meeting?.status === opt.key"
                      class="i-lucide-check size-3 ml-2 flex-shrink-0"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-n-weak" />

        <!-- Convidados -->
        <div class="px-5 py-4">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-2 uppercase tracking-wide"
          >
            {{ t('AGENDA.MODAL.GUESTS') }}
          </h4>
          <div class="flex flex-col gap-1">
            <div
              class="flex flex-wrap gap-1.5 outline outline-1 outline-offset-[-1px] rounded-lg px-2.5 py-2 bg-n-background min-h-[40px] cursor-text transition-all duration-500 ease-in-out"
              :class="
                attendeeEmailError
                  ? 'outline-n-ruby-8 hover:outline-n-ruby-9'
                  : 'outline-n-weak hover:outline-n-slate-6 focus-within:outline-n-brand'
              "
              @click="attendeeInputRef?.focus()"
            >
              <span
                v-for="email in automaticAttendeeEmails"
                :key="email"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-teal-2 text-n-teal-11"
                :title="t('AGENDA.MODAL.GUEST_AUTOMATIC')"
              >
                {{ email }}
              </span>
              <span
                v-for="email in manualAttendeeEmails"
                :key="email"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-blue-2 text-n-blue-11"
              >
                {{ email }}
                <button
                  type="button"
                  class="hover:opacity-70 transition-opacity"
                  @click.stop="removeAttendee(email)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
              <input
                ref="attendeeInputRef"
                v-model="attendeeInput"
                type="email"
                :placeholder="t('AGENDA.MODAL.GUEST_EMAIL_PLACEHOLDER')"
                class="flex-1 min-w-32 text-sm bg-transparent outline-none border-none shadow-none appearance-none reset-base text-n-slate-12 placeholder:text-n-slate-10 !bg-transparent !shadow-none !outline-none !border-none"
                @keydown="onAttendeeKeydown"
                @blur="addAttendeeFromInput"
              />
            </div>
            <p
              v-if="attendeeEmailError"
              class="text-label-small text-n-ruby-9 truncate"
            >
              {{ attendeeEmailError }}
            </p>
          </div>
        </div>

        <!-- Link -->
        <div class="px-5 py-4 border-t border-n-weak">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-2 uppercase tracking-wide"
          >
            {{ t('AGENDA.MODAL.LINK') }}
          </h4>
          <div class="relative">
            <input
              ref="locationInputRef"
              v-model="locationValue"
              type="text"
              :placeholder="t('AGENDA.MODAL.LOCATION_ADD_PLACEHOLDER')"
              class="w-full border border-n-weak rounded-lg pl-3 py-2 text-sm bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand placeholder:text-n-slate-9"
              :class="isLocationUrl ? 'cursor-pointer pr-16' : 'pr-9'"
              @mousedown="onLocationMousedown"
              @blur="onLocationBlur"
            />
            <div
              v-if="isLocationUrl"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-2"
            >
              <button
                type="button"
                class="text-n-slate-10 hover:text-n-brand transition-colors"
                :title="t('AGENDA.MODAL.EDIT_LOCATION')"
                @mousedown.stop.prevent="editLocation"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <span class="text-n-slate-10 pointer-events-none">
                <span class="i-lucide-arrow-up-right size-4" />
              </span>
            </div>
          </div>
        </div>

        <!-- Notas -->
        <div class="px-5 py-4 border-t border-n-weak">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-2 uppercase tracking-wide"
          >
            {{ t('AGENDA.MODAL.NOTES') }}
          </h4>
          <textarea
            v-model="notesValue"
            rows="4"
            :placeholder="t('AGENDA.MODAL.NOTES_ADD_PLACEHOLDER')"
            class="w-full text-sm text-n-slate-12 bg-n-surface-2 rounded-lg px-3 py-2 resize-none border border-transparent focus:border-n-brand focus:outline-none placeholder:text-n-slate-10"
            @blur="onNotesBlur"
          />
        </div>

        <!-- Comentários -->
        <div class="px-5 py-4 border-t border-n-weak">
          <MeetingComments v-if="meeting" :meeting-id="meeting.id" />
        </div>

        <div class="border-t border-n-weak" />

        <!-- Histórico -->
        <div class="px-5 py-4">
          <h4
            class="text-xs font-medium text-n-slate-10 mb-3 uppercase tracking-wide"
          >
            {{ t('AGENDA.MODAL.HISTORY') }}
          </h4>
          <div v-if="activities.length" class="flex flex-col gap-3">
            <div
              v-for="activity in [...activities].reverse()"
              :key="activity.id"
              class="flex items-start gap-2.5"
            >
              <span
                class="size-4 mt-0.5 flex-shrink-0"
                :class="[
                  activityConfig(activity.event_type).icon,
                  activityConfig(activity.event_type).color,
                ]"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm text-n-slate-12">
                  {{ activityConfig(activity.event_type).text(activity) }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ formatDate(activity.created_at) }}
                  <span v-if="activity.user" class="text-n-slate-9">
                    · {{ activity.user.name }}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10 py-2">
            {{ t('AGENDA.MODAL.NO_ACTIVITY') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
